/**
 * Composite an image and a transparent annotation canvas into a single
 * PNG (default) or JPEG data URL rendered at the image's natural resolution.
 */

import { AnnotationLayer } from './annotation-layer';
import { hydrateAnnotations } from './serialization';
import type { AnnotationData } from './types';

export function compositeImageWithAnnotations(
    image: HTMLImageElement,
    annotationCanvas: HTMLCanvasElement,
    mimeType: 'image/jpeg' | 'image/png' = 'image/png'
): string {
    const width = image.naturalWidth;
    const height = image.naturalHeight;

    const offscreen = document.createElement('canvas');
    offscreen.width = width;
    offscreen.height = height;
    const ctx = offscreen.getContext('2d');
    if (!ctx) return image.src;

    // The annotation canvas is sized to the image's natural resolution, so both
    // draw 1:1 — no scaling, and the export matches what the user saw on screen.
    ctx.drawImage(image, 0, 0, width, height);
    ctx.drawImage(annotationCanvas, 0, 0);

    return offscreen.toDataURL(mimeType);
}

/**
 * Bake serialized annotations onto a base image without an open editor —
 * used to refresh a snapshot's cached composite when its base image changes
 * (e.g. switching the overlay/no-overlay variant).
 */
export async function bakeAnnotations(
    baseImageUrl: string,
    annotations: AnnotationData[],
    mimeType: 'image/jpeg' | 'image/png' = 'image/png'
): Promise<string> {
    const image = await loadImage(baseImageUrl);

    // Detached container: the layer only needs its canvas backing store,
    // which is sized to the image's natural resolution.
    const layer = new AnnotationLayer(document.createElement('div'));
    try {
        layer.setImageSize(image.naturalWidth, image.naturalHeight);
        hydrateAnnotations(layer, annotations);
        layer.render();
        return compositeImageWithAnnotations(image, layer.getCanvas(), mimeType);
    } finally {
        layer.dispose();
    }
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load snapshot image'));
        img.src = src;
    });
}
